import React, { useState, useEffect } from "react";
import mapImg from "../assets/map.png";
import "./DelhiLiveMapSection.css";

const DelhiLiveMapSection = () => {
  const [selectedFilter, setSelectedFilter] = useState(null); // 'good', 'moderate', 'poor', 'severe', or null
  const [activeNode, setActiveNode] = useState(null);

  // Live metrics simulation state
  const [stats, setStats] = useState({
    peopleSupported: 3117,
    airPerMinute: 3484.45,
    airPerHour: 209066.96,
    freshAirToday: 1392503.46,
    totalAirTreated: 3068825.46,
    cumulativePeople: 229761,
  });

  // Active AtmosPOLE Nodes: 10 Online, 2 Awaiting Connection, 4 Offline
  const nodes = [
    // 10 ONLINE NODES (Green)
    {
      id: "node-anand-vihar",
      name: "Anand Vihar Main Road",
      status: "online",
      statusLabel: "Online",
      aqi: 42,
      pm25: 14,
      pm10: 32,
      poles: 8,
      x: 77.2,
      y: 42.0,
    },
    {
      id: "node-civil-lines",
      name: "Civil Lines",
      status: "online",
      statusLabel: "Online",
      aqi: 38,
      pm25: 12,
      pm10: 28,
      poles: 6,
      x: 33.2,
      y: 22.8,
    },
    {
      id: "node-cp",
      name: "Connaught Place",
      status: "online",
      statusLabel: "Online",
      aqi: 45,
      pm25: 15,
      pm10: 35,
      poles: 14,
      x: 50.2,
      y: 39.5,
    },
    {
      id: "node-lodhi",
      name: "Lodhi Estate",
      status: "online",
      statusLabel: "Online",
      aqi: 40,
      pm25: 13,
      pm10: 30,
      poles: 10,
      x: 55.8,
      y: 69.5,
    },
    {
      id: "node-okhla",
      name: "Okhla Phase 3",
      status: "online",
      statusLabel: "Online",
      aqi: 48,
      pm25: 18,
      pm10: 38,
      poles: 11,
      x: 80.5,
      y: 59.6,
    },
    {
      id: "node-mayur-vihar",
      name: "Mayur Vihar",
      status: "online",
      statusLabel: "Online",
      aqi: 44,
      pm25: 16,
      pm10: 34,
      poles: 7,
      x: 73.8,
      y: 54.0,
    },
    {
      id: "node-rohini",
      name: "Rohini Sector 9",
      status: "online",
      statusLabel: "Online",
      aqi: 36,
      pm25: 11,
      pm10: 26,
      poles: 9,
      x: 22.5,
      y: 20.0,
    },
    {
      id: "node-chanakyapuri",
      name: "Chanakyapuri",
      status: "online",
      statusLabel: "Online",
      aqi: 35,
      pm25: 10,
      pm10: 24,
      poles: 12,
      x: 38.5,
      y: 58.5,
    },
    {
      id: "node-saket",
      name: "Saket District Centre",
      status: "online",
      statusLabel: "Online",
      aqi: 46,
      pm25: 17,
      pm10: 36,
      poles: 10,
      x: 52.0,
      y: 82.0,
    },
    {
      id: "node-hauz-khas",
      name: "Hauz Khas",
      status: "online",
      statusLabel: "Online",
      aqi: 39,
      pm25: 13,
      pm10: 29,
      poles: 8,
      x: 44.0,
      y: 72.0,
    },

    // 2 AWAITING CONNECTION NODES (Orange)
    {
      id: "node-secretariat",
      name: "Secretariat",
      status: "awaiting",
      statusLabel: "Awaiting Connection",
      aqi: 168,
      pm25: 78,
      pm10: 154,
      poles: 12,
      x: 47.8,
      y: 44.5,
    },
    {
      id: "node-dhaula-kuan",
      name: "Dhaula Kuan",
      status: "awaiting",
      statusLabel: "Awaiting Connection",
      aqi: 175,
      pm25: 82,
      pm10: 160,
      poles: 15,
      x: 28.7,
      y: 56.0,
    },

    // 4 OFFLINE NODES (Red)
    {
      id: "node-rajghat",
      name: "Rajghat",
      status: "offline",
      statusLabel: "Offline",
      aqi: 385,
      pm25: 210,
      pm10: 345,
      poles: 6,
      x: 58.6,
      y: 39.2,
    },
    {
      id: "node-ito",
      name: "ITO Vikas Marg",
      status: "offline",
      statusLabel: "Offline",
      aqi: 412,
      pm25: 235,
      pm10: 375,
      poles: 8,
      x: 61.5,
      y: 44.0,
    },
    {
      id: "node-kashmere-gate",
      name: "Kashmere Gate",
      status: "offline",
      statusLabel: "Offline",
      aqi: 395,
      pm25: 220,
      pm10: 360,
      poles: 7,
      x: 48.0,
      y: 26.5,
    },
    {
      id: "node-karol-bagh",
      name: "Karol Bagh",
      status: "offline",
      statusLabel: "Offline",
      aqi: 420,
      pm25: 245,
      pm10: 390,
      poles: 10,
      x: 41.5,
      y: 36.0,
    },
  ];

  // Subtle live ticking increment
  useEffect(() => {
    const interval = setInterval(() => {
      setStats((prev) => ({
        ...prev,
        freshAirToday: prev.freshAirToday + 0.35,
        totalAirTreated: prev.totalAirTreated + 0.65,
      }));
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  const toggleFilter = (status) => {
    setSelectedFilter((prev) => (prev === status ? null : status));
  };

  const formatNumberWithDecimals = (num, decimals = 2) => {
    return num.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  const formatInteger = (num) => {
    return Math.round(num).toLocaleString("en-US");
  };

  const onlineCount = nodes.filter((n) => n.status === "online").length;
  const awaitingCount = nodes.filter((n) => n.status === "awaiting").length;
  const offlineCount = nodes.filter((n) => n.status === "offline").length;

  return (
    <div className="delhi-map-wrapper">
      {/* Background Map Layer */}
      <div className="delhi-map-bg-layer">
        <img
          src={mapImg}
          alt="Delhi Live Air Quality Network Map"
          className="delhi-map-bg-img"
        />
        <div className="delhi-map-vignette" />
        <div className="delhi-map-top-fade" />
        <div className="delhi-map-bottom-fade" />
      </div>

      <div className="delhi-map-container">
        {/* Interactive Map Stage */}
        <div className="delhi-map-stage">
          {/* Top-Right Legend Bar (Online, Awaiting Connection, Offline) */}
          <div className="delhi-map-legend-bar">
            {[
              { status: "online", label: "Online", count: onlineCount },
              { status: "awaiting", label: "Awaiting Connection", count: awaitingCount },
              { status: "offline", label: "Offline", count: offlineCount },
            ].map((item) => (
              <div
                key={item.status}
                className={`delhi-legend-item ${
                  selectedFilter && selectedFilter !== item.status ? "dimmed" : ""
                }`}
                onClick={() => toggleFilter(item.status)}
                title={`Filter ${item.label} (${item.count})`}
              >
                <div className={`delhi-legend-dot ${item.status}`} />
                <span className="delhi-legend-label">
                  {item.label}
                </span>
              </div>
            ))}
          </div>

          {/* Air Quality Nodes */}
          {nodes.map((node) => {
            const isDimmed = selectedFilter && selectedFilter !== node.status;
            const isNodeActive = activeNode === node.id;

            return (
              <div
                key={node.id}
                className={`delhi-map-marker ${node.status} ${
                  isDimmed ? "dimmed" : ""
                } ${isNodeActive ? "active" : ""}`}
                style={{
                  left: `${node.x}%`,
                  top: `${node.y}%`,
                }}
                onMouseEnter={() => setActiveNode(node.id)}
                onMouseLeave={() => setActiveNode(null)}
                onClick={() => setActiveNode(isNodeActive ? null : node.id)}
              >
                <div className="delhi-marker-container">
                  <div className="delhi-marker-glow" />
                  <div className="delhi-marker-ring" />
                  <div className="delhi-marker-ring delhi-marker-ring-delay" />
                  <div className="delhi-marker-core" />
                </div>

                {/* Hover Tooltip */}
                <div className="delhi-marker-tooltip">
                  <div className="delhi-tooltip-header">
                    <span className="delhi-tooltip-title">{node.name}</span>
                    <span className={`delhi-tooltip-badge ${node.status}`}>
                      {node.statusLabel}
                    </span>
                  </div>
                  <div className="delhi-tooltip-stats">
                    <div className="delhi-tooltip-stat-item">
                      <span className="delhi-tooltip-stat-label">AQI Score</span>
                      <span className="delhi-tooltip-stat-val">{node.aqi}</span>
                    </div>
                    <div className="delhi-tooltip-stat-item">
                      <span className="delhi-tooltip-stat-label">PM 2.5</span>
                      <span className="delhi-tooltip-stat-val">{node.pm25} µg/m³</span>
                    </div>
                    <div className="delhi-tooltip-stat-item">
                      <span className="delhi-tooltip-stat-label">Active Poles</span>
                      <span className="delhi-tooltip-stat-val">{node.poles} Units</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Floating Dashboard Panel */}
        <div className="delhi-dashboard-card">
          {/* Header Row */}
          <div className="delhi-dashboard-header">
            <h3 className="delhi-dashboard-title">
              WHAT IS DELHI BREATHING RIGHT NOW?
            </h3>

            <div className="delhi-dashboard-network-badge">
              <span className="delhi-badge-accent">Breathe easier. Live better.</span>
              <span className="delhi-badge-sub">
                Measured across our active filtration network.
              </span>
            </div>
          </div>

          {/* Subheader Line */}
          <div className="delhi-dashboard-subline">
            <span className="delhi-subline-tag">HUMAN BREATHING BENEFIT</span>
            <span className="delhi-subline-desc">
              Cleaner air delivered. Measurable impact. Better lives.
            </span>
          </div>

          {/* 6 Stats Grid */}
          <div className="delhi-metrics-grid">
            {/* Metric 1: People Supported */}
            <div className="delhi-metric-item">
              <span className="delhi-metric-label">PEOPLE SUPPORTED RIGHT NOW</span>
              <div className="delhi-metric-val-row">
                <span className="delhi-metric-number cyan-text">
                  ~{formatInteger(stats.peopleSupported)}
                </span>
              </div>
              <span className="delhi-metric-subtext">Across the network</span>
            </div>

            {/* Metric 2: Cleaner Air Per Person / min */}
            <div className="delhi-metric-item">
              <span className="delhi-metric-label">CLEANER AIR PER PERSON</span>
              <div className="delhi-metric-val-row">
                <span className="delhi-metric-icon cyan">
                  {/* Wind / Airflow Streamline SVG */}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2" />
                  </svg>
                </span>
                <span className="delhi-metric-number">
                  {formatNumberWithDecimals(stats.airPerMinute, 2)}
                </span>
              </div>
              <span className="delhi-metric-subtext">cu ft per minute</span>
            </div>

            {/* Metric 3: Cleaner Air Per Person / hr */}
            <div className="delhi-metric-item">
              <span className="delhi-metric-label">CLEANER AIR PER PERSON</span>
              <div className="delhi-metric-val-row">
                <span className="delhi-metric-icon cyan">
                  {/* 3D Isometric Cube SVG */}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                    <line x1="12" y1="22.08" x2="12" y2="12" />
                  </svg>
                </span>
                <span className="delhi-metric-number">
                  {formatNumberWithDecimals(stats.airPerHour, 2)}
                </span>
              </div>
              <span className="delhi-metric-subtext">cu ft per hour</span>
            </div>

            {/* Metric 4: Fresh Air Generated Today */}
            <div className="delhi-metric-item">
              <span className="delhi-metric-label">FRESH AIR GENERATED TODAY</span>
              <div className="delhi-metric-val-row">
                <span className="delhi-metric-icon green">
                  {/* Eco Leaf / Seedling SVG */}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 20A7 7 0 0 1 4 13C4 7 10 3 20 3c0 10-4 16-11 16Z" />
                    <path d="M4 13c3.5 0 8 1.5 10.5 5.5" />
                  </svg>
                </span>
                <span className="delhi-metric-number">
                  {formatNumberWithDecimals(stats.freshAirToday, 2)}
                </span>
              </div>
            </div>

            {/* Metric 5: Total Air Treated Today */}
            <div className="delhi-metric-item">
              <span className="delhi-metric-label">TOTAL AIR TREATED TODAY</span>
              <div className="delhi-metric-val-row">
                <span className="delhi-metric-icon amber">
                  {/* Dual Exchange Flow SVG */}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 3 4 7l4 4" />
                    <path d="M4 7h16" />
                    <path d="m16 21 4-4-4-4" />
                    <path d="M20 17H4" />
                  </svg>
                </span>
                <span className="delhi-metric-number">
                  {formatNumberWithDecimals(stats.totalAirTreated, 2)}
                </span>
              </div>
            </div>

            {/* Metric 6: Cumulative People Served */}
            <div className="delhi-metric-item">
              <span className="delhi-metric-label">CUMULATIVE PEOPLE SERVED</span>
              <div className="delhi-metric-val-row">
                <span className="delhi-metric-icon green">
                  {/* People / Users Group SVG */}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </span>
                <span className="delhi-metric-number">
                  {formatInteger(stats.cumulativePeople)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DelhiLiveMapSection;
